/** Names and ids, as Pokémon Showdown writes them */

/** "Iron Hands" -> "ironhands", the same as Showdown's toID */
export function toID(text: unknown): string {
	return `${text ?? ''}`.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Words that come first in spreadsheet-style names but are a forme suffix in Showdown's ("Mega Charizard X") */
const FORME_PREFIXES: Record<string, string> = {
	mega: 'mega', primal: 'primal', alolan: 'alola', galarian: 'galar', hisuian: 'hisui', paldean: 'paldea',
};

/**
 * The ids a single Pokémon's name could mean as a draft sheet writes it: itself, the male forme without the
 * "-M" Showdown leaves off ("Basculegion-M", "Meowstic-M"), and the forme written first ("Eternal-Floette").
 * With `halves`, either half on its own too, for the Mega of a forme ("Mega Eternal-Floette" is Floette's): only
 * there, so a misspelt forme ("Raichu-Alolan") is never quietly taken for its base Pokémon.
 */
function nameIds(name: string, halves = false) {
	const ids = [toID(name)];
	if (/-M$/i.test(name)) ids.push(toID(name.slice(0, -2)));
	const parts = name.split('-');
	if (parts.length === 2) {
		ids.push(toID(`${parts[1]}-${parts[0]}`));
		if (halves) ids.push(toID(parts[1]), toID(parts[0]));
	}
	return ids;
}

/**
 * The ids a name could mean, most likely first: the name itself, then the spreadsheet-style reading
 * ("Mega Charizard X" -> "charizardmegax", "Alolan Ninetales" -> "ninetalesalola", "Basculegion-M" ->
 * "basculegion", "Eternal-Floette" -> "floetteeternal"), and for a Mega, its male forme's ("Mega Meowstic" ->
 * "meowsticmmega") and its base Pokémon's ("Mega Eternal-Floette" -> "floettemega")
 */
export function candidateIds(name: string): string[] {
	const cleaned = `${name}`.replace(/[*’]/g, '').trim();
	const words = cleaned.split(/\s+/);
	const ids = words.length === 1 ? nameIds(cleaned) : [toID(cleaned)];
	const prefix = FORME_PREFIXES[toID(words[0])];
	if (prefix && words.length > 1) {
		const suffix = toID(words.slice(2).join(''));
		for (const base of nameIds(words[1]!, prefix === 'mega')) ids.push(base + prefix + suffix);
		if (prefix === 'mega') ids.push(`${toID(words[1])}m${prefix}${suffix}`);
	}
	return [...new Set(ids.filter(id => id))];
}
